/**
 * banfListUtils — reine Ableitungen für die neue BANF-Listenansicht (Design-Handoff 28.07.).
 * Alles client-seitig aus den bereits geladenen purchase_requests berechnet — Zähler/KPIs
 * sind IMMER live abgeleitet, nie hartkodiert (explizite Design-Vorgabe).
 */

/** Betreff einer BANF: Kurztext der ersten Position (+ Positionszahl). */
export const banfSubject = (r) => {
  const items = Array.isArray(r.items) ? r.items : [];
  const first = (items[0]?.description || '').trim();
  if (!first) return `BANF #${r.id}`;
  return first;
};

/** Anzeige-Status: Genehmigungsstatus + SAP-Transfer als eigener Zustand 'transferred'. */
export const banfDisplayStatus = (r) => {
  if (r.deleted_at) return 'deleted';
  if (r.sap_transfer_status === 'posted') return 'transferred';
  return r.status; // pending | approved | rejected
};

/** Wartezeit seit Anlage, menschenlesbar (de/en über Sprachschlüssel im Aufrufer). */
export const waitingSince = (r, language) => {
  const d = r.created_at ? new Date(r.created_at) : null;
  if (!d || isNaN(d.getTime())) return '';
  const ms = Date.now() - d.getTime();
  const hours = Math.floor(ms / 36e5);
  if (hours < 1) return language === 'de' ? 'unter 1 Std.' : 'under 1 hr';
  if (hours < 48) return language === 'de' ? `seit ${hours} Std.` : `for ${hours} hrs`;
  const days = Math.floor(hours / 24);
  return language === 'de' ? `seit ${days} Tagen` : `for ${days} days`;
};

/** Betrag de-DE mit 2 Nachkommastellen (Design: immer de-DE, € nachgestellt). */
export const fmtAmount = (v, currency = 'EUR') =>
  `${Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency === 'EUR' ? '€' : currency}`;

/** KPI-Kacheln — live aus der (ungefilterten) Liste berechnet. */
export const computeKpis = (all) => {
  const requests = all.filter((r) => !r.deleted_at);
  const pending = requests.filter((r) => r.status === 'pending');
  const approved = requests.filter((r) => r.status === 'approved' && r.processed_at && r.created_at);
  const posted = requests.filter((r) => r.sap_transfer_status === 'posted');
  const failed = requests.filter((r) => r.sap_transfer_status === 'failed');

  const pendingSum = pending.reduce((s, r) => s + Number(r.total_amount || 0), 0);
  const volumeSum = requests.reduce((s, r) => s + Number(r.total_amount || 0), 0);
  const leadTimes = approved.map((r) => (new Date(r.processed_at) - new Date(r.created_at)) / 864e5)
    .filter((d) => isFinite(d) && d >= 0);
  const avgLead = leadTimes.length
    ? Math.round((leadTimes.reduce((a, b) => a + b, 0) / leadTimes.length) * 10) / 10
    : null;

  return {
    pendingCount: pending.length,
    pendingSum,
    avgLeadDays: avgLead,
    volumeSum,
    volumeCount: requests.length,
    postedCount: posted.length,
    failedCount: failed.length,
  };
};

/** Live-Suche über Nr, Betreff, Anforderer, Abteilung, KST, SAP-Nr, Lieferant, Material. */
export const matchesQuery = (r, q) => {
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  const items = Array.isArray(r.items) ? r.items : [];
  const hay = [
    `#${r.id}`, String(r.id), banfSubject(r), r.requester_name, r.requester_department,
    r.requester_cost_center, r.sap_banfn,
    ...items.flatMap((it) => [it.description, it.preferred_vendor, it.fixed_vendor, it.vendor, it.matnr, it.material]),
  ].filter(Boolean).join(' ').toLowerCase();
  return hay.includes(needle);
};

/** Spalten-Definitionen (feste + optionale; Sichtbarkeit optionaler pro User persistiert). */
export const OPTIONAL_COLUMNS = ['anforderer', 'kostl', 'matkl', 'lieferant', 'belegart', 'erstellt', 'sap'];
export const DEFAULT_OPTIONAL_COLUMNS = ['anforderer', 'sap'];

/** Erster Lieferant / erste Warengruppe / Belegart einer BANF (für optionale Spalten). */
export const banfVendor = (r) => {
  const items = Array.isArray(r.items) ? r.items : [];
  for (const it of items) {
    const v = it.fixed_vendor || it.preferred_vendor || it.vendor;
    if (v) return String(v);
  }
  return '';
};
export const banfMatkl = (r) => {
  const items = Array.isArray(r.items) ? r.items : [];
  for (const it of items) {
    const v = it.material_group || it.product_group || it.category;
    if (v) return String(v);
  }
  return '';
};
export const banfBelegart = (r) => {
  const items = Array.isArray(r.items) ? r.items : [];
  return String(items[0]?.belegart || 'NB');
};
